import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FileText, BookOpen, FileCheck, Download } from 'lucide-react';
import { resources } from '../data/mock';

const Resources = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', 'CTEM', 'AI Security', 'BAS', 'Risk Management', 'Zero Trust', 'Red Team'];

  const filteredResources = selectedCategory === 'all' 
    ? resources 
    : resources.filter(r => r.category === selectedCategory);

  const iconMap = {
    Article: FileText,
    Whitepaper: FileCheck,
    Guide: BookOpen,
    'Case Study': FileCheck
  };

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section className="resources-hero">
        <div className="resources-hero-content">
          <Badge className="hero-badge">Knowledge Center</Badge>
          <h1 className="resources-hero-title">
            Resources & Insights
          </h1>
          <p className="resources-hero-description">
            Expert insights on AI-powered cybersecurity, CTEM, compliance, and threat intelligence
          </p>
        </div>
      </section>

      {/* Featured Whitepaper */}
      <section className="section featured-section">
        <div className="section-content">
          <Card className="featured-card">
            <CardHeader>
              <Badge className="featured-badge">Featured Whitepaper</Badge>
              <CardTitle className="featured-title">
                AI-Driven Continuous Exposure Management for Indian BFSI
              </CardTitle>
              <CardDescription>
                Comprehensive guide to implementing CTEM in banking and financial services
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="featured-content">
                <div className="featured-text">
                  <p className="featured-description">
                    This whitepaper explores how Indian banks and financial institutions can leverage AI-powered Continuous Threat Exposure Management 
                    to meet RBI cybersecurity guidelines while reducing risk and improving security posture.
                  </p>
                  <ul className="featured-list">
                    <li>RBI Master Direction alignment strategies</li>
                    <li>DPDP India compliance for BFSI</li>
                    <li>AI-powered risk quantification</li>
                    <li>Real-world BFSI implementation case studies</li>
                  </ul>
                </div>
                <div className="featured-download">
                  <Download className="download-icon" />
                  <p className="download-text">Download this exclusive whitepaper</p>
                  <form className="download-form">
                    <input 
                      type="email" 
                      placeholder="Enter your work email" 
                      className="download-input"
                      required
                    />
                    <button type="submit" className="download-button">
                      Download Whitepaper
                    </button>
                  </form>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="section">
        <div className="section-content">
          <div className="section-header">
            <h2 className="section-title">Knowledge Library</h2>
            <p className="section-description">
              Browse our collection of articles, whitepapers, and guides
            </p>
          </div>

          {/* Category Filter */}
          <Tabs defaultValue="all" className="resources-tabs">
            <TabsList className="resources-tabs-list">
              {categories.map((category) => (
                <TabsTrigger 
                  key={category} 
                  value={category}
                  onClick={() => setSelectedCategory(category)}
                  className="resources-tab"
                >
                  {category === 'all' ? 'All' : category}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Resources Grid */}
          <div className="resources-grid">
            {filteredResources.map((resource) => {
              const IconComponent = iconMap[resource.type] || FileText;
              
              return (
                <Card key={resource.id} className="resource-card">
                  <CardHeader>
                    <div className="resource-header">
                      <Badge variant="secondary" className="resource-type-badge">
                        {resource.type}
                      </Badge>
                      <span className="resource-time">{resource.readTime} read</span>
                    </div>
                    <IconComponent className="resource-icon" />
                    <CardTitle className="resource-title">{resource.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="resource-description">{resource.description}</p>
                    <Badge className="resource-category-badge">{resource.category}</Badge>
                    <a href={resource.url} className="resource-link">
                      Read More →
                    </a>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Popular Topics */}
      <section className="section topics-section">
        <div className="section-content">
          <div className="section-header">
            <h2 className="section-title">Popular Topics</h2>
            <p className="section-description">
              Most searched cybersecurity topics
            </p>
          </div>
          <div className="topics-grid">
            <Card className="topic-card">
              <CardHeader>
                <CardTitle>What is CTEM India?</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Understanding Continuous Threat Exposure Management and its importance for Indian enterprises</p>
              </CardContent>
            </Card>
            <Card className="topic-card">
              <CardHeader>
                <CardTitle>AI TRiSM Framework</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Managing AI model risks, security, and trustworthiness in enterprise environments</p>
              </CardContent>
            </Card>
            <Card className="topic-card">
              <CardHeader>
                <CardTitle>BAS vs Pentesting</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Why continuous breach simulation outperforms annual penetration testing</p>
              </CardContent>
            </Card>
            <Card className="topic-card">
              <CardHeader>
                <CardTitle>Zero Trust for BFSI</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Implementing Zero Trust Architecture in Indian banking and financial services</p>
              </CardContent>
            </Card>
            <Card className="topic-card">
              <CardHeader>
                <CardTitle>DPDP Compliance</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Guide to Digital Personal Data Protection Act compliance for enterprises</p>
              </CardContent>
            </Card>
            <Card className="topic-card">
              <CardHeader>
                <CardTitle>Cyber Risk Quantification</CardTitle>
              </CardHeader>
              <CardContent>
                <p>Financial modeling approach to cybersecurity risk for insurance and compliance</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="section newsletter-section">
        <div className="section-content">
          <Card className="newsletter-card">
            <CardHeader>
              <CardTitle className="newsletter-title">Stay Updated</CardTitle>
              <CardDescription className="newsletter-description">
                Subscribe to our newsletter for the latest insights on AI security and cybersecurity trends
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="newsletter-form">
                <input 
                  type="email" 
                  placeholder="Enter your email" 
                  className="newsletter-input"
                  required
                />
                <button type="submit" className="newsletter-button">
                  Subscribe
                </button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Resources;
